import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Typography } from 'antd';
import {
  ApartmentOutlined,
  DeploymentUnitOutlined,
  LoadingOutlined,
  SearchOutlined,
  ThunderboltOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  buildGroups,
  flattenGroups,
  fuzzyMatch,
  type PaletteItem,
  type PaletteItemKind,
} from './paletteModel';

const { Text } = Typography;

/** Async pool provider: given the (debounced) query, return candidate rows. Never throws. */
export type PaletteSource = (query: string) => Promise<PaletteItem[]>;

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** Imperative rows (New flow, Open help, Start tour…) — supplied by the host. */
  actions: readonly PaletteItem[];
  /** Navigation rows derived from the surface registry. */
  surfaces: readonly PaletteItem[];
  /** Most-recently-used rows, shown only while the query is empty. */
  recents: readonly PaletteItem[];
  searchFlows: PaletteSource;
  searchEntities: PaletteSource;
  onSelect: (item: PaletteItem) => void;
  /** Debounce for the remote sources. 250 ms = below the 300 ms typing-pause perception floor. */
  debounceMs?: number;
}

const KIND_ICON: Record<PaletteItemKind, React.ReactNode> = {
  surface: <RightOutlined />,
  action: <ThunderboltOutlined />,
  flow: <ApartmentOutlined />,
  entity: <DeploymentUnitOutlined />,
};

/** Bold the matched characters so the operator can see WHY a row ranked (signal, not decoration). */
function Highlighted({ text, query }: { text: string; query: string }) {
  const positions = useMemo(() => new Set(fuzzyMatch(query, text)?.positions ?? []), [text, query]);
  if (positions.size === 0) return <>{text}</>;
  return (
    <>
      {Array.from(text).map((ch, i) =>
        positions.has(i) ? (
          <mark key={i} className="ol-cmdk__hit">
            {ch}
          </mark>
        ) : (
          <span key={i}>{ch}</span>
        ),
      )}
    </>
  );
}

/**
 * The global command palette (Cmd/Ctrl+K). Presentation + keyboard only: every data source and
 * every side effect is injected by `CommandPaletteHost`, which keeps this component pure enough
 * to test and free of routing/API coupling.
 *
 * Cognitive rationale: an analyst working a queue for eight hours should never hunt a menu for a
 * surface they can name. One chord, type three letters, Enter — Hick's Law collapsed to a single
 * decision, with recents carrying the last five destinations so repeat trips cost nothing.
 */
export function CommandPalette({
  open,
  onClose,
  actions,
  surfaces,
  recents,
  searchFlows,
  searchEntities,
  onSelect,
  debounceMs = 250,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [flows, setFlows] = useState<PaletteItem[]>([]);
  const [entities, setEntities] = useState<PaletteItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset on every open: the palette always starts from a clean slate, never a stale query.
  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
      setActive(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => setDebounced(query), debounceMs);
    return () => clearTimeout(id);
  }, [query, open, debounceMs]);

  // Remote pools. Throttled by the debounce above; results are dropped if a newer query landed.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setBusy(true);
    void Promise.all([searchFlows(debounced), searchEntities(debounced)])
      .then(([f, e]) => {
        if (!alive) return;
        setFlows(f);
        setEntities(e);
      })
      .catch(() => {
        if (alive) {
          setFlows([]);
          setEntities([]);
        }
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [open, debounced, searchFlows, searchEntities]);

  const groups = useMemo(
    () => buildGroups({ query, surfaces, actions, flows, entities, recents }),
    [query, surfaces, actions, flows, entities, recents],
  );
  const flat = useMemo(() => flattenGroups(groups), [groups]);

  // Keep the cursor inside the result set as it shrinks under the operator's fingers.
  useEffect(() => {
    setActive((i) => (flat.length === 0 ? 0 : Math.min(i, flat.length - 1)));
  }, [flat.length]);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [active, groups]);

  const choose = useCallback(
    (item: PaletteItem | undefined) => {
      if (!item) return;
      onSelect(item);
    },
    [onSelect],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((i) => (flat.length === 0 ? 0 : (i + 1) % flat.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => (flat.length === 0 ? 0 : (i - 1 + flat.length) % flat.length));
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActive(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setActive(Math.max(0, flat.length - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        choose(flat[active]);
      }
    },
    [flat, active, choose],
  );

  let index = -1;
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      destroyOnClose
      width={640}
      style={{ top: 72 }}
      styles={{ body: { padding: 0 } }}
      // The palette IS the dialog label; AntD needs an explicit one with no title bar.
      aria-label="Command palette"
      className="ol-cmdk"
      // Modal's focus management focuses its wrapper AFTER the open animation, beating the
      // input's autoFocus — refocus once the animation settles or keys type into a DIV.
      afterOpenChange={(nowOpen) => {
        if (nowOpen) inputRef.current?.focus();
      }}
    >
      <div className="ol-cmdk__search">
        <SearchOutlined aria-hidden style={{ color: 'var(--ol-text-tertiary)' }} />
        <input
          autoFocus
          ref={inputRef}
          className="ol-cmdk__input"
          placeholder="Search surfaces, flows, entities, actions…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded
          aria-controls="ol-cmdk-listbox"
          aria-autocomplete="list"
          aria-label="Command palette search"
          aria-activedescendant={flat[active] ? `ol-cmdk-opt-${active}` : undefined}
          data-ol-palette-input="true"
        />
        {busy && <LoadingOutlined aria-label="Searching" style={{ color: 'var(--ol-text-tertiary)' }} />}
      </div>

      <div className="ol-cmdk__list" id="ol-cmdk-listbox" role="listbox" ref={listRef} tabIndex={-1}>
        {flat.length === 0 && (
          <div className="ol-cmdk__none">
            <Text type="secondary" style={{ fontSize: 12.5 }}>
              Nothing matches “{query}”. Try a surface name (workbench, alerts), a flow name or id,
              or an entity id.
            </Text>
          </div>
        )}
        {groups.map((group) => (
          <div key={group.key} role="group" aria-label={group.title}>
            <div className="ol-cmdk__group">{group.title}</div>
            {group.items.map((item) => {
              index += 1;
              const i = index;
              const isActive = i === active;
              return (
                <div
                  key={item.id}
                  id={`ol-cmdk-opt-${i}`}
                  role="option"
                  aria-selected={isActive}
                  // Explicit name: the label is rendered per-character to highlight the match, so
                  // computing the name from contents would spell it out letter by letter.
                  aria-label={item.hint ? `${item.label} — ${item.hint}` : item.label}
                  data-active={isActive}
                  className={`ol-cmdk__row${isActive ? ' ol-cmdk__row--active' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(item)}
                >
                  <span className="ol-cmdk__icon" aria-hidden>
                    {KIND_ICON[item.kind]}
                  </span>
                  <span className="ol-cmdk__body">
                    <span className="ol-cmdk__label">
                      <Highlighted text={item.label} query={query} />
                    </span>
                    {item.hint && <span className="ol-cmdk__hint">{item.hint}</span>}
                  </span>
                  {item.meta && <span className="ol-cmdk__meta ol-mono">{item.meta}</span>}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="ol-cmdk__footer">
        <span>
          <span className="ol-kbd">↑</span>
          <span className="ol-kbd">↓</span>
          move
        </span>
        <span>
          <span className="ol-kbd">↵</span>
          open
        </span>
        <span>
          <span className="ol-kbd">esc</span>
          close
        </span>
        <span style={{ marginInlineStart: 'auto' }}>
          <span className="ol-kbd">?</span>
          help for this page
        </span>
      </div>
    </Modal>
  );
}
